import { FC, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardStats, getDashboardStats } from "data/adminApi";
import {
  BuildingOffice2Icon,
  DocumentTextIcon,
  FolderIcon,
  NewspaperIcon,
  Squares2X2Icon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";

const cards = [
  { key: "products", label: "Products", to: "/admin/products", icon: Squares2X2Icon },
  { key: "categories", label: "Categories", to: "/admin/categories", icon: FolderIcon },
  { key: "blogs", label: "Blogs", to: "/admin/blogs", icon: NewspaperIcon },
  { key: "clients", label: "Clients", to: "/admin/clients", icon: UserGroupIcon },
  { key: "projects", label: "Projects", to: "/admin/projects", icon: BuildingOffice2Icon },
  { key: "featured_products", label: "Featured", to: "/admin/products", icon: DocumentTextIcon },
] as const;

const AdminDashboard: FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getDashboardStats()
      .then(setStats)
      .catch(() => setError("Could not load dashboard stats."));
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">
        Dashboard
      </h1>
      <p className="mt-1 text-sm text-neutral-500">
        Overview of website content managed from this portal.
      </p>

      {error && (
        <div className="mt-4 rounded-xl bg-red-50 text-red-700 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="mt-8 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {cards.map((card) => (
          <Link
            key={card.key}
            to={card.to}
            className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5 hover:shadow-bcr-card-hover transition-shadow"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-neutral-500">
                {card.label}
              </span>
              <card.icon className="w-5 h-5 text-primary-600" />
            </div>
            <p className="mt-3 text-3xl font-bold text-[#0f1b3d] dark:text-white">
              {stats ? stats[card.key] : "—"}
            </p>
          </Link>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6">
        <h2 className="font-bold text-neutral-900 dark:text-white">
          Quick actions
        </h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to="/admin/products/new"
            className="h-10 px-4 inline-flex items-center rounded-xl bcr-btn-primary text-sm font-semibold"
          >
            Add product
          </Link>
          <Link
            to="/admin/blogs"
            className="h-10 px-4 inline-flex items-center rounded-xl border border-neutral-200 dark:border-neutral-700 text-sm font-semibold"
          >
            Manage blogs
          </Link>
          <Link
            to="/admin/clients"
            className="h-10 px-4 inline-flex items-center rounded-xl border border-neutral-200 dark:border-neutral-700 text-sm font-semibold"
          >
            Manage clients
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
